import React, { useState, useEffect } from "react";
import { db } from "../utils/firebase";
import {
  collection,
  doc,
  onSnapshot,
  getDocs,
  updateDoc,
  where,
  query,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Spinner } from "../components/index.js";
import {
  Bed,
  ArrowRight,
  ArrowLeft,
  Home,
  X,
  Users,
  Building2,
  DoorClosed,
} from "lucide-react";
import { useAuth } from "../hooks/AuthenticationProvider";

const BedComponent = ({ bed, residents, onBedClick, color, canUpdateBeds }) => {
  const resident = residents.find((res) => res.bedLocation === bed.id);

  return (
    <div
      className={`relative group ${
        canUpdateBeds ? "cursor-pointer" : "cursor-not-allowed"
      } transform transition-all duration-300 ${
        canUpdateBeds ? "hover:scale-105" : ""
      } ${resident ? "opacity-100" : "opacity-60 hover:opacity-100"}`}
      onClick={() => {
        if (canUpdateBeds) {
          onBedClick(bed);
        }
      }}
    >
      <div
        className="absolute inset-0 rounded-lg blur-lg transform group-hover:scale-110 transition-transform duration-300"
        style={{ backgroundColor: `${color}1a` }}
      />
      <div
        className="relative p-4 bg-slate-800/50 backdrop-blur-sm rounded-lg border hover:border-opacity-40"
        style={{ borderColor: `${color}33` }}
      >
        <div className="flex items-center gap-2">
          <Bed className="w-5 h-5" style={{ color }} />
          <span className="text-white font-medium">{bed.id}</span>
        </div>
        <div className="mt-2 text-sm">
          {resident ? (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" style={{ color }} />
              <span style={{ color }}>
                {resident.firstName} {resident.lastName}
              </span>
            </div>
          ) : (
            <span className="text-slate-400">Available</span>
          )}
        </div>
      </div>
    </div>
  );
};

const RoomComponent = ({
  room,
  residents,
  onBedClick,
  color,
  canUpdateBeds,
}) => {
  return (
    <div className="p-4 bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-800">
      <div className="flex items-center gap-2 mb-4">
        <DoorClosed className="w-5 h-5" style={{ color }} />
        <h3 className="text-lg font-medium text-white">{room.name}</h3>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {room.beds.map((bed) => (
          <BedComponent
            key={bed.id}
            bed={bed}
            residents={residents}
            onBedClick={onBedClick}
            color={color}
            canUpdateBeds={canUpdateBeds}
          />
        ))}
      </div>
    </div>
  );
};

const FloorComponent = ({
  floor,
  residents,
  onBedClick,
  color,
  canUpdateBeds,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div
          className="p-2 rounded-lg"
          style={{ backgroundColor: `${color}1a` }}
        >
          <Building2 style={{ color }} />
        </div>
        <h2 className="text-xl font-semibold text-white">Floor {floor.id}</h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {floor.rooms.map((room) => (
          <RoomComponent
            key={room.id}
            room={room}
            residents={residents}
            onBedClick={onBedClick}
            color={color}
            canUpdateBeds={canUpdateBeds}
          />
        ))}
      </div>
    </div>
  );
};

const BedManager = () => {
  const navigate = useNavigate();
  const { user, isLoading, organizationData } = useAuth();

  const housesData = organizationData?.housesData || {};
  const organizationName = organizationData?.organizationName || "";

  const calculateOccupancy = (houseName, house) => {
    const occupied = house.occupied || 0;
    const total = house.totalBeds || 0;
    return total > 0 ? Math.round((occupied / total) * 100) : 0;
  };

  if (isLoading) return <Spinner />;
  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{organizationName} • Bed Portal</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Bed Management
              </h1>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {Object.entries(housesData).map(([houseName, house]) => (
              <div key={houseName} className="group relative">
                <div
                  onClick={() => navigate(`/beds/${houseName}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate(`/beds/${houseName}`);
                    }
                  }}
                  className="block relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer"
                  style={{
                    borderColor: house.color,
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderOpacity: "0.2",
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${house.color}1a` }}
                      >
                        <Home style={{ color: house.color }} />
                      </div>
                      <h2 className="text-lg font-semibold text-white">
                        {houseName}
                      </h2>
                    </div>
                    <ArrowRight
                      style={{ color: house.color }}
                      className="transform group-hover:translate-x-1 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex flex-col items-center space-y-6">
                      <div className="relative w-48 h-48">
                        <div
                          className="absolute inset-0 rounded-full"
                          style={{ backgroundColor: `${house.color}1a` }}
                        />
                        <div
                          className="relative w-full h-full rounded-full border-8 flex items-center justify-center"
                          style={{ borderColor: `${house.color}33` }}
                        >
                          <div className="text-center">
                            <p
                              className="text-4xl font-bold"
                              style={{ color: house.color }}
                            >
                              {calculateOccupancy(houseName, house)}%
                            </p>
                            <p className="text-sm text-slate-400">Occupied</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-sm text-slate-400">Available</p>
                          <p
                            className="text-xl font-bold"
                            style={{ color: house.color }}
                          >
                            {house.totalBeds - (house.occupied || 0)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-slate-400">Total Beds</p>
                          <p
                            className="text-xl font-bold"
                            style={{ color: house.color }}
                          >
                            {house.totalBeds}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

const HouseView = () => {
  const { user, isLoading, organizationId, organizationData, checkPermission } =
    useAuth();
  const navigate = useNavigate();
  const { houseName } = useParams();
  const [selectedBed, setSelectedBed] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [residents, setResidents] = useState([]);

  const normalizedHouseName =
    houseName.charAt(0).toUpperCase() + houseName.slice(1);
  const housesData = organizationData?.housesData || {};
  const organizationName = organizationData?.organizationName || "";
  const currentHouse = housesData[houseName];

  const canUpdateBeds = checkPermission("updateBeds");

  useEffect(() => {
    if (!organizationId || !houseName) return;

    // First, do a one-time fetch for initial data
    const fetchInitialResidents = async () => {
      try {
        const residentsRef = collection(
          db,
          `organizations/${organizationId}/residents`
        );
        const q = query(
          residentsRef,
          where("currentResident", "==", true),
          where("house", "==", normalizedHouseName)
        );

        const snapshot = await getDocs(q);
        const residentsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setResidents(residentsList);
      } catch (error) {
        console.error("Error fetching initial residents:", error);
      }
    };

    fetchInitialResidents();

    // Then listen ONLY for changes to bed assignments
    const bedChangesQuery = query(
      collection(db, `organizations/${organizationId}/residents`),
      where("currentResident", "==", true),
      where("house", "==", normalizedHouseName)
    );

    const unsubscribe = onSnapshot(bedChangesQuery, (snapshot) => {
      // We use this flag to avoid processing the initial snapshot twice
      let hasProcessedInitialData = false;

      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();
        const id = change.doc.id;

        // Skip if this is part of the initial data we already fetched
        if (snapshot.metadata?.fromCache && !hasProcessedInitialData) {
          hasProcessedInitialData = true;
          return;
        }

        if (change.type === "added") {
          setResidents((prev) => {
            // Avoid duplicates
            if (prev.some((r) => r.id === id)) return prev;
            return [...prev, { id, ...data }];
          });
        } else if (change.type === "modified") {
          setResidents((prev) =>
            prev.map((res) => (res.id === id ? { id, ...data } : res))
          );
        } else if (change.type === "removed") {
          setResidents((prev) => prev.filter((res) => res.id !== id));
        }
      });
    });

    return () => unsubscribe();
  }, [organizationId, houseName, normalizedHouseName]);

  const handleBedClick = (bed) => {
    if (!canUpdateBeds) return; // Restrict bed selection based on permissions
    setSelectedBed(bed);
    setShowModal(true);
  };

  const handleResidentSelection = async (resident) => {
    try {
      const specificQuery = query(
        collection(db, `organizations/${organizationId}/residents`),
        where("firstName", "==", resident.firstName),
        where("lastName", "==", resident.lastName),
        where("currentResident", "==", true)
      );

      const querySnapshot = await getDocs(specificQuery);
      if (querySnapshot.empty) {
        console.error("No current resident found");
        return;
      }

      const currentDoc = querySnapshot.docs[0];
      await updateDoc(
        doc(db, `organizations/${organizationId}/residents`, currentDoc.id),
        {
          bedLocation: selectedBed.id,
          house: normalizedHouseName,
        }
      );

      setShowModal(false);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  if (isLoading) return <Spinner />;
  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>
          {organizationName} • {houseName} Beds
        </title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="max-w-7xl mx-auto space-y-8 relative">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
            <div className="flex items-center gap-6">
              <div className="flex gap-3">
                <div
                  className="h-16 w-1 rounded-full animate-pulse"
                  style={{
                    background: `linear-gradient(to bottom, ${currentHouse.color}80, ${currentHouse.color}99)`,
                  }}
                />
                <div
                  className="h-16 w-1 rounded-full animate-pulse delay-100 opacity-70"
                  style={{
                    background: `linear-gradient(to bottom, ${currentHouse.color}80, ${currentHouse.color}99)`,
                  }}
                />
                <div
                  className="h-16 w-1 rounded-full animate-pulse delay-200 opacity-40"
                  style={{
                    background: `linear-gradient(to bottom, ${currentHouse.color}80, ${currentHouse.color}99)`,
                  }}
                />
              </div>
              <h2 className="text-4xl font-semibold text-white uppercase">
                {houseName}
              </h2>
            </div>
            <button
              onClick={() => navigate("/beds")}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-800/70 rounded-lg text-slate-300 w-full sm:w-auto"
            >
              <ArrowLeft size={16} />
              <span>Back to Houses</span>
            </button>
          </div>

          {currentHouse?.floors?.map((floor) => (
            <FloorComponent
              key={floor.id}
              floor={floor}
              residents={residents}
              onBedClick={handleBedClick}
              color={currentHouse.color}
              canUpdateBeds={canUpdateBeds}
            />
          ))}
        </div>
      </div>

      {showModal && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] touch-none"
            onClick={() => setShowModal(false)}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 touch-auto overscroll-none">
            <div
              className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] my-auto overflow-y-auto overscroll-contain"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 px-6 py-4 border-b border-slate-700 flex items-center justify-between bg-slate-900/95 backdrop-blur-sm z-[102]">
                <div className="flex items-center gap-3">
                  <div
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${currentHouse.color}1a` }}
                  >
                    <Users style={{ color: currentHouse.color }} size={20} />
                  </div>
                  <h3
                    className="text-xl font-semibold"
                    style={{ color: currentHouse.color }}
                  >
                    Assign Resident
                  </h3>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="relative z-[103] p-2 hover:bg-slate-800 rounded-lg transition-colors touch-auto"
                >
                  <X
                    className="hover:text-slate-300"
                    style={{ color: currentHouse.color }}
                    size={20}
                  />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800 overscroll-contain">
                <ul className="space-y-3">
                  {residents
                    .sort((a, b) => a.firstName.localeCompare(b.firstName)) // Sort by first name
                    .map((resident) => (
                      <li
                        key={resident.id}
                        onClick={() => handleResidentSelection(resident)}
                        className="p-3 bg-slate-800/50 rounded-lg border transition-colors text-slate-300 hover:text-white cursor-pointer"
                        style={{
                          borderColor: `${currentHouse.color}33`,
                          "--hover-border-color": `${currentHouse.color}66`,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.borderColor = `${currentHouse.color}66`)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.borderColor = `${currentHouse.color}33`)
                        }
                      >
                        {resident.firstName} {resident.lastName}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export { BedManager, HouseView };
